/**
 * Service for managing booking price quotes
 */

import { supabase } from "../lib/supabase";
import type {
  BookingQuote,
  BookingQuoteComponent,
  BookingQuoteWithComponents,
} from "../types/bookingQuote";
import type { PricingComponentResult } from "../components/BookingWizard/hooks/useTemplatePricing";
import type { CalculatedPrices } from "../components/BookingWizard/PricingCalculatorTypes";
import i18next from "i18next";
import {
  COMMODITY_INSPECTION_FEE_EUR,
  getEffectiveCommodityInspection,
} from "../utils/commodityInspectionUtils";
import {
  getPostExportLicenseFee,
} from "../utils/postExportLicenseUtils";
import type { BookingQuoteComponent } from "../types/bookingQuote";

const COMMODITY_INSPECTION_QUOTE_TYPE = "commodity_inspection";
const POST_EXPORT_LICENSE_QUOTE_TYPE = "post_export_license";

type QuoteComponentDraft = {
  component_name: string;
  component_description?: string;
  component_type?: string;
  calculation_type: string;
  unit_price: number;
  quantity: number;
  total_price: number;
  is_optional: boolean;
  is_enabled: boolean;
  was_customer_edited: boolean;
  category_group: string;
  display_order: number;
  meta?: Record<string, unknown>;
};

const hasCommodityInspectionComponent = (
  components: Array<{ component_name?: string; component_type?: string | null }>
): boolean =>
  components.some((component) => {
    const name = (component.component_name || "").toLowerCase();
    const type = (component.component_type || "").toLowerCase();
    return (
      type === COMMODITY_INSPECTION_QUOTE_TYPE ||
      name.includes("commodity inspection") ||
      name.includes("iscm")
    );
  });

const fetchBookingCommodityInspectionState = async (bookingId: number) => {
  const { data, error } = await supabase
    .from("bookings")
    .select("commodity_inspection, post_export_license")
    .eq("id", bookingId)
    .single();

  if (error || !data) return null;
  return data;
};

const buildCommodityInspectionQuoteComponent = (
  translate: (
    key: string,
    defaultValue: string,
    options?: Record<string, unknown>
  ) => string,
  displayOrder: number
): QuoteComponentDraft => ({
  component_name: translate(
    "bookingQuote.fallback.commodityInspection",
    "Commodity Inspection"
  ),
  component_type: COMMODITY_INSPECTION_QUOTE_TYPE,
  calculation_type: "per_shipment",
  unit_price: COMMODITY_INSPECTION_FEE_EUR,
  quantity: 1,
  total_price: COMMODITY_INSPECTION_FEE_EUR,
  is_optional: false,
  is_enabled: true,
  was_customer_edited: false,
  category_group: "customs",
  display_order: displayOrder,
});

const enrichQuoteComponentsWithCommodityInspection = async (
  bookingId: number,
  components: QuoteComponentDraft[],
  totalCost: number,
  translate: (
    key: string,
    defaultValue: string,
    options?: Record<string, unknown>
  ) => string
): Promise<{ components: QuoteComponentDraft[]; totalCost: number }> => {
  if (hasCommodityInspectionComponent(components)) {
    return { components, totalCost };
  }

  const booking = await fetchBookingCommodityInspectionState(bookingId);
  if (!booking || !getEffectiveCommodityInspection(booking)) {
    return { components, totalCost };
  }

  const inspectionComponent = buildCommodityInspectionQuoteComponent(
    translate,
    components.length
  );

  return {
    components: [...components, inspectionComponent],
    totalCost: totalCost + COMMODITY_INSPECTION_FEE_EUR,
  };
};

const hasPostExportLicenseComponent = (
  components: Array<{ component_name?: string; component_type?: string | null }>
): boolean =>
  components.some((component) => {
    const name = (component.component_name || "").toLowerCase();
    const type = (component.component_type || "").toLowerCase();
    return (
      type === POST_EXPORT_LICENSE_QUOTE_TYPE ||
      name.includes("post export license")
    );
  });

const buildPostExportLicenseQuoteComponent = (
  translate: (
    key: string,
    defaultValue: string,
    options?: Record<string, unknown>
  ) => string,
  displayOrder: number,
  amount: number
): QuoteComponentDraft => ({
  component_name: translate(
    "bookingQuote.fallback.postExportLicense",
    "Post Export License"
  ),
  component_type: POST_EXPORT_LICENSE_QUOTE_TYPE,
  calculation_type: "per_shipment",
  unit_price: amount,
  quantity: 1,
  total_price: amount,
  is_optional: false,
  is_enabled: true,
  was_customer_edited: false,
  category_group: "customs",
  display_order: displayOrder,
});

const enrichQuoteComponentsWithPostExportLicense = async (
  bookingId: number,
  components: QuoteComponentDraft[],
  totalCost: number,
  translate: (
    key: string,
    defaultValue: string,
    options?: Record<string, unknown>
  ) => string
): Promise<{ components: QuoteComponentDraft[]; totalCost: number }> => {
  if (hasPostExportLicenseComponent(components)) {
    return { components, totalCost };
  }

  const booking = await fetchBookingCommodityInspectionState(bookingId);
  const fee = booking ? getPostExportLicenseFee(booking) : null;
  if (!fee) {
    return { components, totalCost };
  }

  const licenseComponent = buildPostExportLicenseQuoteComponent(
    translate,
    components.length,
    fee
  );

  return {
    components: [...components, licenseComponent],
    totalCost: totalCost + fee,
  };
};

interface ComponentOverrides {
  [componentId: number]: {
    enabled: boolean;
    value?: number;
  };
}

interface SaveQuoteData {
  bookingId: number;
  pricingResult?: {
    components: PricingComponentResult[];
    totalCost: number;
    currency: string;
    matchedTemplate?: any | null;
    matchingCriteria?: string[];
  };
  calculatedPrices?: CalculatedPrices;
  transportType?: number;
  bookingType?: number;
  incoterm?: number;
  originPort?: number;
  destinationPort?: number;
  products?: Array<{
    id?: number;
    name: string;
    hs_code: string | null;
    gross_weight: number | null;
    cbm: number | null;
    value: number | null;
    pieces?: number | null;
    cartons?: number | null;
  }>;
}

/**
 * Save a booking price quote
 */
export const saveBookingQuote = async (
  data: SaveQuoteData
): Promise<{ success: boolean; error?: string; quoteId?: number }> => {
  try {
    const {
      bookingId,
      pricingResult,
      calculatedPrices,
      transportType,
      bookingType,
      incoterm,
      originPort,
      destinationPort,
      products = [],
    } = data;

    const translate = (
      key: string,
      defaultValue: string,
      options: Record<string, unknown> = {}
    ) => i18next.t(key, { defaultValue, ...options });

    // Determine pricing source and calculate components
    let quoteComponents: QuoteComponentDraft[] = [];
    
    let currency = "EUR";
    let totalCost = 0;

    // Use template-based pricing if available
    if (pricingResult && pricingResult.components.length > 0) {
      currency = pricingResult.currency;
      totalCost = pricingResult.totalCost;
      
      quoteComponents = pricingResult.components.map((comp, index) => ({
        component_name: comp.name,
        component_description: comp.description,
        component_type: comp.componentType,
        calculation_type: comp.calculationType,
        unit_price: comp.value,
        quantity: comp.quantity,
        total_price: comp.totalPrice,
        is_optional: comp.isOptional,
        is_enabled: comp.isEnabled,
        was_customer_edited: comp.canCustomerEdit,
        category_group: comp.categoryGroup,
        display_order: index,
        meta: {
          unit_price: comp.value,
          quantity: comp.quantity,
        },
      }));
    } 
    // Fall back to hardcoded pricing structure
    else if (calculatedPrices) {
      totalCost = calculatedPrices.totalCost;

      // Create components from CalculatedPrices
      let displayOrder = 0;

      // Transport costs
      if (calculatedPrices.transportCost > 0) {
        quoteComponents.push({
          component_name: translate("bookingQuote.fallback.transportCosts", "Transport Costs"),
          component_type: "transport",
          calculation_type: "per_shipment",
          unit_price: calculatedPrices.transportCost,
          quantity: 1,
          total_price: calculatedPrices.transportCost,
          is_optional: false,
          is_enabled: true,
          was_customer_edited: false,
          category_group: "transport",
          display_order: displayOrder++,
        });
      }

      // Diesel surcharge
      if (calculatedPrices.dieselSurcharge > 0) {
        quoteComponents.push({
          component_name: translate("bookingQuote.fallback.dieselSurcharge", "Diesel surcharge"),
          component_description: translate(
            "bookingQuote.fallback.dieselSurchargeDescription",
            "Additional fee for fuel costs"
          ),
          component_type: "transport",
          calculation_type: "percentage",
          unit_price: calculatedPrices.dieselSurcharge,
          quantity: 1,
          total_price: calculatedPrices.dieselSurcharge,
          is_optional: false,
          is_enabled: true,
          was_customer_edited: false,
          category_group: "transport",
          display_order: displayOrder++,
        });
      }

      // Customs clearance
      if (calculatedPrices.customsClearance > 0) {
        quoteComponents.push({
          component_name: translate("bookingQuote.fallback.customsClearance", "Customs clearance"),
          component_type: "customs",
          calculation_type: "per_shipment",
          unit_price: calculatedPrices.customsClearance,
          quantity: 1,
          total_price: calculatedPrices.customsClearance,
          is_optional: false,
          is_enabled: true,
          was_customer_edited: false,
          category_group: "customs",
          display_order: displayOrder++,
        });
      }

      // Handling fees
      if (calculatedPrices.handlingFees > 0) {
        quoteComponents.push({
          component_name: translate("bookingQuote.fallback.handling", "Handling fees"),
          component_type: "handling",
          calculation_type: "per_shipment",
          unit_price: calculatedPrices.handlingFees,
          quantity: 1,
          total_price: calculatedPrices.handlingFees,
          is_optional: false,
          is_enabled: true,
          was_customer_edited: false,
          category_group: "handling",
          display_order: displayOrder++,
        });
      }

      // Delivery costs
      if (calculatedPrices.deliveryCost > 0) {
        quoteComponents.push({
          component_name: translate("bookingQuote.fallback.delivery", "Delivery"),
          component_type: "delivery",
          calculation_type: "per_shipment",
          unit_price: calculatedPrices.deliveryCost,
          quantity: 1,
          total_price: calculatedPrices.deliveryCost,
          is_optional: false,
          is_enabled: true,
          was_customer_edited: false,
          category_group: "delivery",
          display_order: displayOrder++,
        });
      }

      // Pickup fee
      if (calculatedPrices.pickupFee > 0) {
        quoteComponents.push({
          component_name: translate("bookingQuote.fallback.pickupFee", "Pickup fee"),
          component_type: "delivery",
          calculation_type: "per_shipment",
          unit_price: calculatedPrices.pickupFee,
          quantity: 1,
          total_price: calculatedPrices.pickupFee,
          is_optional: true,
          is_enabled: true,
          was_customer_edited: false,
          category_group: "delivery",
          display_order: displayOrder++,
        });
      }

      // Export clearance license
      if (calculatedPrices.exportClearanceLicense > 0) {
        quoteComponents.push({
          component_name: translate("bookingQuote.fallback.exportClearanceLicense", "Export Clearance License"),
          component_type: "customs",
          calculation_type: "per_shipment",
          unit_price: calculatedPrices.exportClearanceLicense,
          quantity: 1,
          total_price: calculatedPrices.exportClearanceLicense,
          is_optional: true,
          is_enabled: true,
          was_customer_edited: false,
          category_group: "customs",
          display_order: displayOrder++,
        });
      }

      const totalWeight = products.reduce((sum, product) => sum + (product.gross_weight || 0), 0);
      const totalCBM = products.reduce((sum, product) => sum + (product.cbm || 0), 0);
      const totalCartons = products.reduce((sum, product) => sum + (product.cartons || 0), 0);
      const uniqueHsCodes = new Set(
        products
          .map((product) => {
            const code = product.hs_code;
            if (code === null || code === undefined) {
              return null;
            }
            return String(code).trim() || null;
          })
          .filter((code) => code !== null)
      );
      const uniqueHsCodesCount = uniqueHsCodes.size;

      const addMetaRow = (
        nameKey: string,
        value: number,
        description?: string
      ) => {
        quoteComponents.push({
          component_name: translate(nameKey, nameKey),
          component_description: description,
          component_type: "meta",
          calculation_type: "per_shipment",
          unit_price: value,
          quantity: 1,
          total_price: value,
          is_optional: false,
          is_enabled: true,
          was_customer_edited: false,
          category_group: "other",
          display_order: quoteComponents.length,
          meta: {
            value,
            description,
          },
        });
      };

      addMetaRow(
        "bookingQuote.meta.totalWeight",
        Number(totalWeight.toFixed(2)),
        translate(
          "bookingQuote.meta.totalWeightDescription",
          "{{value}} kg",
          { value: totalWeight.toFixed(2) }
        )
      );
      addMetaRow(
        "bookingQuote.meta.totalVolume",
        Number(totalCBM.toFixed(3)),
        translate(
          "bookingQuote.meta.totalVolumeDescription",
          "{{value}} CBM",
          { value: totalCBM.toFixed(3) }
        )
      );
      addMetaRow(
        "bookingQuote.meta.totalCartons",
        totalCartons,
        translate(
          "bookingQuote.meta.totalCartonsDescription",
          "{{value}} cartons",
          { value: totalCartons }
        )
      );
      addMetaRow(
        "bookingQuote.meta.uniqueHsCodes",
        uniqueHsCodesCount,
        translate(
          "bookingQuote.meta.uniqueHsCodesDescription",
          "{{value}} HS codes",
          { value: uniqueHsCodesCount }
        )
      );
    } else {
      return {
        success: false,
        error: "No pricing data provided for quote",
      };
    }

    const enrichedPricing = await enrichQuoteComponentsWithCommodityInspection(
      bookingId,
      quoteComponents,
      totalCost,
      translate
    );
    quoteComponents = enrichedPricing.components;
    totalCost = enrichedPricing.totalCost;

    const enrichedWithLicense = await enrichQuoteComponentsWithPostExportLicense(
      bookingId,
      quoteComponents,
      totalCost,
      translate
    );
    quoteComponents = enrichedWithLicense.components;
    totalCost = enrichedWithLicense.totalCost;

    // Create the quote record
    const { data: quote, error: quoteError } = await supabase
      .from("booking_price_quotes")
      .insert({
        booking_id: bookingId,
        currency,
        total_cost: totalCost,
        quote_generated_at: new Date().toISOString(),
        transport_type_id: transportType,
        booking_type_id: bookingType,
        incoterm_id: incoterm,
        origin_port_id: originPort,
        destination_port_id: destinationPort,
        matched_template_name: pricingResult?.matchedTemplate?.name,
        matching_criteria: pricingResult?.matchingCriteria || [],
      })
      .select()
      .single();

    if (quoteError) {
      console.error("Error creating quote:", quoteError);
      return {
        success: false,
        error: `Failed to create quote: ${quoteError.message}`,
      };
    }

    // Create quote components
    if (quoteComponents.length > 0) {
      const { error: componentsError } = await supabase
        .from("booking_quote_components")
        .insert(
          quoteComponents.map((comp) => ({
            quote_id: quote.id,
            component_name: comp.component_name,
            component_description: comp.component_description,
            component_type: comp.component_type,
            calculation_type: comp.calculation_type,
            unit_price: comp.unit_price,
            quantity: comp.quantity,
            total_price: comp.total_price,
            currency,
            is_optional: comp.is_optional,
            is_enabled: comp.is_enabled,
            was_customer_edited: comp.was_customer_edited,
            category_group: comp.category_group,
            display_order: comp.display_order,
          }))
        );

      if (componentsError) {
        console.error("Error creating quote components:", componentsError);
        // Try to clean up the quote
        await supabase.from("booking_price_quotes").delete().eq("id", quote.id);
        return {
          success: false,
          error: `Failed to create quote components: ${componentsError.message}`,
        };
      }
    }

    return {
      success: true,
      quoteId: quote.id,
    };
  } catch (error) {
    console.error("Error saving booking quote:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Get a booking quote with all components
 */
export const getBookingQuote = async (
  bookingId: number
): Promise<{
  success: boolean;
  data?: BookingQuoteWithComponents;
  error?: string;
}> => {
  try {
    const { data: quote, error: quoteError } = await supabase
      .from("booking_price_quotes")
      .select("*")
      .eq("booking_id", bookingId)
      .single();

    if (quoteError) {
      if (quoteError.code === "PGRST116") {
        // No quote found
        return {
          success: false,
          error: "No quote found for this booking",
        };
      }
      console.error("Error fetching quote:", quoteError);
      return {
        success: false,
        error: quoteError.message,
      };
    }

    // Fetch components
    const { data: components, error: componentsError } = await supabase
      .from("booking_quote_components")
      .select("*")
      .eq("quote_id", quote.id)
      .order("display_order", { ascending: true });

    if (componentsError) {
      console.error("Error fetching quote components:", componentsError);
      return {
        success: false,
        error: componentsError.message,
      };
    }

    let enrichedComponents = (components || []) as BookingQuoteComponent[];
    let enrichedTotalCost = quote.total_cost;

    const translate = (
      key: string,
      defaultValue: string,
      options: Record<string, unknown> = {}
    ) => i18next.t(key, { defaultValue, ...options });

    if (!hasCommodityInspectionComponent(enrichedComponents)) {
      const booking = await fetchBookingCommodityInspectionState(bookingId);
      if (booking && getEffectiveCommodityInspection(booking)) {
        const draft = buildCommodityInspectionQuoteComponent(
          translate,
          enrichedComponents.length
        );
        enrichedComponents = [
          ...enrichedComponents,
          {
            id: -1,
            quote_id: quote.id,
            component_name: draft.component_name,
            component_description: draft.component_description,
            component_type: draft.component_type,
            calculation_type: draft.calculation_type,
            unit_price: draft.unit_price,
            quantity: draft.quantity,
            total_price: draft.total_price,
            currency: quote.currency,
            is_optional: draft.is_optional,
            is_enabled: draft.is_enabled,
            was_customer_edited: draft.was_customer_edited,
            category_group: draft.category_group as BookingQuoteComponent["category_group"],
            display_order: draft.display_order,
            created_at: quote.created_at,
          },
        ];
        enrichedTotalCost += COMMODITY_INSPECTION_FEE_EUR;
      }
    }

    if (!hasPostExportLicenseComponent(enrichedComponents)) {
      const booking = await fetchBookingCommodityInspectionState(bookingId);
      const fee = booking ? getPostExportLicenseFee(booking) : null;
      if (fee) {
        const draft = buildPostExportLicenseQuoteComponent(
          translate,
          enrichedComponents.length,
          fee
        );
        enrichedComponents = [
          ...enrichedComponents,
          {
            id: -2,
            quote_id: quote.id,
            component_name: draft.component_name,
            component_description: draft.component_description,
            component_type: draft.component_type,
            calculation_type: draft.calculation_type,
            unit_price: draft.unit_price,
            quantity: draft.quantity,
            total_price: draft.total_price,
            currency: quote.currency,
            is_optional: draft.is_optional,
            is_enabled: draft.is_enabled,
            was_customer_edited: draft.was_customer_edited,
            category_group: draft.category_group as BookingQuoteComponent["category_group"],
            display_order: draft.display_order,
            created_at: quote.created_at,
          },
        ];
        enrichedTotalCost += fee;
      }
    }

    return {
      success: true,
      data: {
        ...quote,
        total_cost: enrichedTotalCost,
        components: enrichedComponents,
      },
    };
  } catch (error) {
    console.error("Error fetching booking quote:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

/**
 * Check if a booking has a quote
 */
export const hasBookingQuote = async (
  bookingId: number
): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from("booking_price_quotes")
      .select("id")
      .eq("booking_id", bookingId)
      .single();

    if (error && error.code !== "PGRST116") {
      // PGRST116 is "not found", which is fine
      console.error("Error checking for quote:", error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error("Error checking for quote:", error);
    return false;
  }
};


